import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { NavBar } from "@/components/navbar";
import {Footer} from "@/components/SiteFooter";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BarChart, CalendarClock, ChevronRight, Clock, DollarSign, Filter, LineChart, Search, ShieldCheck, SlidersHorizontal } from "lucide-react";
import { initialModules, SavingsType } from "@/data/modules";
import { calculateTimeSavingsForSubmodule, calculateCostSavingsForSubmodule } from "@/utils/savingsCalculations";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

type SavingsFilter = SavingsType | 'ALL';

const LearnMorePage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [savingsFilter, setSavingsFilter] = useState<SavingsFilter>('ALL');
  
  const allSubmodules = initialModules.flatMap(module =>
    module.subModules.map(submodule => ({
      ...submodule,
      uniqueId: `${module.id}-${submodule.id}`,
      moduleName: module.name,
      moduleId: module.id
    }))
  );
  
  const filteredSubmodules = allSubmodules
    .filter(submodule => {
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        return (
          submodule.name.toLowerCase().includes(searchLower) ||
          submodule.moduleName.toLowerCase().includes(searchLower) ||
          submodule.features.some(f => f.toLowerCase().includes(searchLower))
        );
      }
      return true;
    })
    .filter(submodule => {
      if (savingsFilter !== 'ALL') {
        return submodule.linkToSavings === savingsFilter;
      }
      return true;
    })
    .sort((a, b) => b.originalHours * b.efficiency - a.originalHours * a.efficiency)
    .slice(0, 40); // Increased limit to show more submodules
  
  const handleExploreSubmodule = (moduleId: string, submoduleId: string) => {
    navigate(`/visualizer?module=${moduleId}&submodule=${submoduleId}`);
  };
  
  // Helper function to get savings icon based on savings type
  const getSavingsIcon = (savingsType: SavingsType) => {
    switch(savingsType) {
      case SavingsType.TIME:
        return <Clock className="h-5 w-5 text-blue-600" />;
      case SavingsType.COST:
        return <DollarSign className="h-5 w-5 text-green-600" />;
      case SavingsType.ERROR:
        return <ShieldCheck className="h-5 w-5 text-purple-600" />;
      case SavingsType.ROI:
        return <LineChart className="h-5 w-5 text-amber-600" />;
      default:
        return <BarChart className="h-5 w-5 text-gray-600" />;
    }
  };
  
  // Helper function to get color class based on savings type
  const getSavingsColorClass = (savingsType: SavingsType) => {
    switch(savingsType) {
      case SavingsType.TIME:
        return "bg-blue-50 border-blue-200";
      case SavingsType.COST:
        return "bg-green-50 border-green-200";
      case SavingsType.ERROR:
        return "bg-purple-50 border-purple-200";
      case SavingsType.ROI:
        return "bg-amber-50 border-amber-200";
      default:
        return "bg-gray-50 border-gray-200";
    }
  };

  return (
    <div className="min-h-screen flex flex-col pt-16">
      <Helmet>
        <title>Explore Process Optimizations | ESGWize</title>
        <meta name="description" content="Explore AI-optimized compliance workflows and see detailed cost and time savings" />
      </Helmet>
      
      <NavBar />


   <main className="flex-grow overflow-hidden">
                    <div className="container mx-auto px-4 py-8">
                        <div className="max-w-5xl mx-auto">

                          <Card className="p-6 border-none shadow-none">
        <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-4">
          See More <br/>
          <span id="workflow-text"
                className="bg-gradient-to-r from-green-300 to-green-600 bg-clip-text text-transparent mt-2 pe-2">Compliance Processes</span>
        </h1>

                                        <p className="text-lg text-slate-600 mb-6 max-w-xl ">
              Browse our library of AI-enhanced compliance workflows
            </p>


      </Card>


                        </div>
                    </div>





        <section className="container mx-auto px-4">

          
          <div className="max-w-5xl mx-auto">
            {/* Search and filter bar */}
            <div className="flex flex-wrap gap-3 items-center mb-6">
              <div className="relative flex-grow max-w-md">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <Input 
                  type="text" 
                  placeholder="Search processes..." 
                  className="pl-9 h-9"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              <div className="flex flex-wrap gap-2">
                <Badge 
                  variant={savingsFilter === 'ALL' ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => setSavingsFilter('ALL')}
                >
                  All
                </Badge>
                <Badge 
                  variant={savingsFilter === SavingsType.TIME ? "default" : "outline"}
                  className="cursor-pointer bg-blue-100 text-blue-800 hover:bg-blue-200 border-blue-200"
                  onClick={() => setSavingsFilter(SavingsType.TIME)}
                >
                  <Clock className="h-3 w-3 mr-1" />
                  Time Saving
                </Badge>
                <Badge 
                  variant={savingsFilter === SavingsType.COST ? "default" : "outline"}
                  className="cursor-pointer bg-green-100 text-green-800 hover:bg-green-200 border-green-200"
                  onClick={() => setSavingsFilter(SavingsType.COST)}
                >
                  <DollarSign className="h-3 w-3 mr-1" />
                  Cost Saving
                </Badge>
                <Badge 
                  variant={savingsFilter === SavingsType.ERROR ? "default" : "outline"}
                  className="cursor-pointer bg-purple-100 text-purple-800 hover:bg-purple-200 border-purple-200"
                  onClick={() => setSavingsFilter(SavingsType.ERROR)}
                >
                  <ShieldCheck className="h-3 w-3 mr-1" />
                  Error Reduction
                </Badge>
              </div>
            </div>
            
            {/* Process cards in a flat grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {filteredSubmodules.map((submodule) => {
                const costSavings = calculateCostSavingsForSubmodule(submodule);
                
                return (
                  <Card 
                    key={submodule.uniqueId} 
                    className={`border hover:shadow-lg transition-all cursor-pointer ${getSavingsColorClass(submodule.linkToSavings)}`}
                    onClick={() => handleExploreSubmodule(submodule.moduleId, submodule.id)}
                  >
                    <CardHeader className="p-3 pb-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          {getSavingsIcon(submodule.linkToSavings)}
                          <span className="text-xs text-slate-500 truncate max-w-[100px]">{submodule.moduleName}</span>
                        </div>
                        <div className="bg-slate-100 px-2 py-0.5 rounded text-xs font-medium">
                          {submodule.efficiency}% faster
                        </div>
                      </div>
                      <CardTitle className="text-sm mt-1.5 line-clamp-2">{submodule.name}</CardTitle>
                    </CardHeader>
                    
                    <CardContent className="p-3 pt-0">
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-xs">{submodule.steps.length}→{submodule.steps_with_ai.length} steps</span>
                        <span className="text-xs font-medium text-green-700">€{costSavings.toFixed(0)}/yr</span>
                      </div>
                      
                      <div className="flex w-full justify-between items-center mt-2">
                        <div className="flex gap-0.5">
                          {Array(5).fill(0).map((_, i) => (
                            <div 
                              key={`${submodule.id}-impl-${i}`}
                              className={`w-1.5 h-1.5 rounded-full ${i < submodule.implementation ? 'bg-purple-600' : 'bg-gray-200'}`} 
                            />
                          ))}
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 px-2 text-xs font-medium"
                        >
                          View
                          <ChevronRight className="ml-0.5 h-3 w-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            
            {filteredSubmodules.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <Filter className="h-8 w-8 mx-auto mb-2 opacity-40" />
                <p>No processes match your search criteria.</p>
                <Button 
                  variant="link" 
                  onClick={() => {
                    setSearchQuery("");
                    setSavingsFilter('ALL');
                  }}
                >
                  Clear filters
                </Button>
              </div>
            )}
          </div>
        </section>
        
        <section className="container mx-auto px-4 py-12">
          <div className="max-w-lg mx-auto bg-emerald-50 rounded-lg p-6 text-center">
            <h2 className="text-xl font-bold mb-2 text-slate-900">
              Create Your Own AI Workflow
            </h2>
            <p className="text-sm text-slate-600 mb-4">
              Combine processes to build a custom solution
            </p>
            <Button 
              onClick={() => navigate('/customize')}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Start Building
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default LearnMorePage;